import { useCallback, useEffect, useRef } from 'react'
import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query'
import { useGetFlagTypes } from '@/service-library/hooks/flag-types'
import {
  DocFlagChange,
  NotificationChange,
  NotificationChipChange,
  NotificationRowChange,
  NotificationValueChange,
  TagChange,
  ValueFlagChange,
} from '@/types/doc-changes'
import {
  DocumentRow,
  DocumentRowValue,
  DocumentChip,
  Document,
} from '@/types/documents'
import { DocumentFlag, RowValueFlag, FlagType } from '@/types/flags'
import PaginatedResponse from '@/types/paginated-response'
import { showErrorSnackbar, showWarningSnackbar } from '@/utils/snackbars'
import { useQueryKeysMapContext } from '@/components/providers/QueryKeyProvider'
import { getRowsByGrid } from '@/components/validation/providers/DocumentRowsProvider'
import {
  buildFieldIdentifier,
  FieldIdentifier,
  useDocumentsChangeSetsContext,
} from '@/components/validation/providers/DocumentsChangeSetsProvider'

export function updateTagIds(currentTagIds: string[], tagChanges: TagChange[]) {
  const tagsToAdd = new Set<string>()
  const tagsToRemove = new Set<string>()

  tagChanges.forEach(({ op, project_tag_id }) => {
    ;(op === 'add' ? tagsToAdd : tagsToRemove).add(project_tag_id)
  })

  return [
    ...currentTagIds.filter((tagId) => !tagsToRemove.has(tagId)),
    ...[...tagsToAdd].filter((tagId) => !currentTagIds.includes(tagId)),
  ]
}

type FlagChange = DocFlagChange | ValueFlagChange
type Flag = DocumentFlag | RowValueFlag

function updateFlags(
  flagChanges: FlagChange[],
  existingFlags: Flag[],
  flagTypes: FlagType[],
  documentId?: string,
) {
  if (!flagChanges.length) return existingFlags

  const flagChangesMap = new Map(
    flagChanges.reduce<[string, FlagChange][]>(
      (acc, flagChange) => [...acc, [flagChange.id, flagChange]],
      [],
    ),
  )

  const updatedFlags = existingFlags.reduce<Flag[]>((acc, flag) => {
    const flagChange = flagChangesMap.get(flag.id)

    if (!flagChange) {
      acc.push(flag)
    } else {
      if (flagChange.op !== 'delete') {
        acc.push({
          ...flag,
          ...flagChange,
        })
      }
      flagChangesMap.delete(flag.id)
    }

    return acc
  }, [])

  if (flagChangesMap.size) {
    flagChangesMap.values().forEach((flagChange) => {
      if (flagChange.op === 'delete') return

      const flagType = flagTypes.find(
        ({ id }) => flagChange.flag_type_id === id,
      )
      const flag = documentId
        ? { ...flagChange, flag_type: flagType, document_id: documentId }
        : { ...flagChange, flag_type: flagType }
      updatedFlags.push(flag as Flag)
    })
  }

  return updatedFlags
}

// The lineage slots a manual edit writes to. If any key is present the change
// touched the manual value, so it came from the user, not the rules engine.
const MANUAL_VALUE_KEYS = [
  'manual_value',
  'manual_ref_id',
  'manual_value_json',
] as const

const SYSTEM_VALUE_KEYS = [
  'rule_value',
  'rule_ref_id',
  'rule_value_json',
  'final_value',
  'final_ref_id',
  'final_value_json',
] as const

/**
 * A value change is a per-sub-field diff — the rules engine omits any lineage
 * slot that didn't change. So the change came from the system when it carries a
 * rule/final slot and no manual slot at all.
 *
 * Key presence is what matters, not the value: `manual_value: null` means the
 * system cleared the manual value, which is still a change to the manual slot.
 */
export function isSystemOriginatedValueChange(
  valueChange: NotificationValueChange,
) {
  if (MANUAL_VALUE_KEYS.some((key) => Object.hasOwn(valueChange, key)))
    return false
  return SYSTEM_VALUE_KEYS.some((key) => Object.hasOwn(valueChange, key))
}

function requiresInvalidatingData({
  message,
  owner_org_id,
  values,
  rows,
  tags,
  doc_flags,
  id_changes,
  chips,
}: NotificationChange) {
  // If there are no changes or if there is a message (error or warning), we want to
  // refetch in case something in the cache is wrong.
  return (
    !!message ||
    (!owner_org_id &&
      !values.length &&
      !rows.length &&
      !tags.length &&
      !doc_flags.length &&
      !id_changes.length &&
      !chips.length)
  )
}

function mergeObjectArrays<
  T,
  K extends keyof T,
  N extends keyof (T[K] extends Array<infer U> ? U : never),
>(
  prevData: T[],
  newData: T[],
  identifierKey: K,
  fieldDataThatNeedsMerge?: {
    keyInMainObject: K
    identifierKey: N
  },
) {
  const mergedItems = new Map()

  prevData.forEach((item) => mergedItems.set(item[identifierKey], item))

  newData.forEach((item) => {
    const existingItem = mergedItems.get(item[identifierKey])
    const newItem = {
      ...existingItem,
      ...item,
    }
    if (fieldDataThatNeedsMerge && existingItem) {
      const fieldKey = fieldDataThatNeedsMerge.keyInMainObject
      newItem[fieldKey] = mergeObjectArrays(
        existingItem[fieldKey],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        item[fieldKey] as any[],
        fieldDataThatNeedsMerge.identifierKey,
      )
    }
    mergedItems.set(item[identifierKey], newItem)
  })

  return Array.from(mergedItems.values())
}

export type UseApplyDocumentChangesOptions = {
  documentId?: string
  documentQueryKey: QueryKey
  chipsListQueryKey: QueryKey
  rowsListQueryKey: QueryKey
  rowValuesListQueryKey: QueryKey
}

export default function useApplyDocumentChanges({
  documentId,
  documentQueryKey,
  chipsListQueryKey,
  rowsListQueryKey,
  rowValuesListQueryKey,
}: UseApplyDocumentChangesOptions) {
  const queryClient = useQueryClient()
  const [queryKeysMapContext] = useQueryKeysMapContext()
  const {
    documentsChangeSetIdsRef,
    setDocumentLastChangeSetSentAt,
    clearAllFieldsBeingUpdated,
    markFieldsChangedBySystem,
    clearSystemChanges,
    registerChangeApplier,
  } = useDocumentsChangeSetsContext()
  const combinedNotificationChangeRef = useRef<NotificationChange>(undefined)

  const { flagTypes } = useGetFlagTypes({
    filters: {
      limit: '100',
    },
  })

  const resetData = useCallback(() => {
    if (!documentId) return
    queryClient.invalidateQueries({ queryKey: documentQueryKey })
    queryClient.invalidateQueries({ queryKey: chipsListQueryKey })
    queryClient.invalidateQueries({ queryKey: rowsListQueryKey })
    queryClient.invalidateQueries({ queryKey: rowValuesListQueryKey })
    Object.entries(queryKeysMapContext).forEach(([mapKeys, mapQueryKey]) => {
      if (mapKeys.includes(documentId)) {
        queryClient.invalidateQueries({ queryKey: mapQueryKey })
      }
    })
    clearAllFieldsBeingUpdated()
  }, [
    documentId,
    queryClient,
    documentQueryKey,
    chipsListQueryKey,
    rowsListQueryKey,
    rowValuesListQueryKey,
    queryKeysMapContext,
    clearAllFieldsBeingUpdated,
  ])

  const updateDataCache = useCallback(
    async (data: NotificationChange) => {
      if (!documentId) return
      const {
        message,
        owner_org_id,
        values,
        rows,
        tags,
        doc_flags,
        id_changes,
        chips,
      } = data

      // If there are no changes or if there is a message (error or warning), we want to
      // refetch in case something in the cache is wrong.
      if (requiresInvalidatingData(data)) {
        if (message?.level === 'error') showErrorSnackbar(message.text)
        if (message?.level === 'warning') showWarningSnackbar(message.text)
        resetData()
        return
      }

      if (owner_org_id || doc_flags.length || tags.length) {
        // #region Update Document (Org and Flags)
        await queryClient.cancelQueries({ queryKey: documentQueryKey })
        queryClient.setQueryData<Document>(documentQueryKey, (prev) => {
          if (!prev) return prev
          return {
            ...prev,
            owner_org_id: owner_org_id || prev.owner_org_id,
            document_flags: doc_flags.length
              ? updateFlags(
                  doc_flags,
                  prev.document_flags || [],
                  flagTypes,
                  prev.id,
                )
              : prev.document_flags || [],
            project_tags_ids: updateTagIds(prev.project_tags_ids || [], tags),
          } as Document
        })
      }

      // #region Update Rows
      if (rows.length) {
        await queryClient.cancelQueries({ queryKey: rowsListQueryKey })

        queryClient.setQueryData<InfiniteData<PaginatedResponse<DocumentRow>>>(
          rowsListQueryKey,
          (old) => {
            if (!old) return old
            const rowChangesForRowsMap = new Map(
              rows.reduce<[string, NotificationRowChange][]>(
                (acc, rowChange) => [...acc, [rowChange.id, rowChange]],
                [],
              ),
            )

            const pages = old.pages.map((page) => {
              if (!rowChangesForRowsMap.size) return page
              return {
                ...page,
                results: page.results.reduce<DocumentRow[]>(
                  (acc, storedRow) => {
                    const rowChange = rowChangesForRowsMap.get(storedRow.id)
                    if (!rowChange) {
                      acc.push(storedRow)
                    } else {
                      if (rowChange.op !== 'delete') {
                        acc.push({
                          ...storedRow,
                          ...rowChange,
                        } as DocumentRow)
                      }
                      rowChangesForRowsMap.delete(storedRow.id)
                    }
                    return acc
                  },
                  [],
                ),
              }
            })
            // A change can name a row that isn't in the cache — a page the
            // user never scrolled to. Only a new or updated row is worth
            // adding; a delete for a row we don't have is already done.
            const newRows: DocumentRow[] = []
            rowChangesForRowsMap.forEach((rowChange) => {
              if (rowChange.op === 'delete') return
              newRows.push({
                ...rowChange,
                project_grid_id: rowChange.grid_id,
                document_id: documentId,
              } as DocumentRow)
            })

            if (newRows.length) {
              const lastPage = pages[pages.length - 1]
              pages[pages.length - 1] = {
                ...lastPage,
                results: [...lastPage.results, ...newRows],
              }
            }
            return {
              ...old,
              pages,
            }
          },
        )

        const updatedRowsData =
          queryClient.getQueryData<
            InfiniteData<PaginatedResponse<DocumentRow>>
          >(rowsListQueryKey)

        const allRows =
          updatedRowsData?.pages?.reduce<DocumentRow[]>(
            (acc, page) => [...acc, ...page.results],
            [],
          ) ?? []

        // Sort by row number just in case the cache is out of order
        const rowsByGrid = getRowsByGrid(
          allRows.sort((a, b) => a.row_number - b.row_number),
        )

        // Each grid's table runs its own rows query, so the document-wide
        // list above never reaches them. Walk the registered tables: a grid
        // that just lost its last row has dropped out of `rowsByGrid`, and
        // its table still needs emptying.
        const tableKeyPrefix = `table-rows-${documentId}-`
        Object.entries(queryKeysMapContext).forEach(
          async ([mapKey, tableQueryKey]) => {
            if (!mapKey.startsWith(tableKeyPrefix)) return
            const gridId = mapKey.slice(tableKeyPrefix.length)

            await queryClient.cancelQueries({ queryKey: tableQueryKey })

            queryClient.setQueryData<
              InfiniteData<PaginatedResponse<DocumentRow>>
            >(tableQueryKey, (prev) =>
              prev
                ? {
                    ...prev,
                    pages: [
                      {
                        ...prev.pages[0],
                        results: rowsByGrid[gridId] ?? [],
                      },
                    ],
                  }
                : undefined,
            )
          },
        )
      }

      const deletedRowsIds = rows
        .filter(({ op }) => op === 'delete')
        .map(({ id }) => id)

      if (values.length || deletedRowsIds.length || id_changes.length) {
        // #region Update Row Values
        await queryClient.cancelQueries({ queryKey: rowValuesListQueryKey })
        queryClient.setQueryData<
          InfiniteData<PaginatedResponse<DocumentRowValue>>
        >(rowValuesListQueryKey, (old) => {
          if (!old) return old
          const valueChangesForRowValuesMap = new Map(
            values.reduce<[string, NotificationValueChange][]>(
              (acc, valueChange) => [...acc, [valueChange.id, valueChange]],
              [],
            ),
          )

          const valueIdChangesMap = id_changes.reduce<Record<string, string>>(
            (acc, idChange) => {
              if (idChange.type === 'value') {
                // the old id would be the id sent in the request
                acc[idChange.old_id] = idChange.new_id
              }
              return acc
            },
            {},
          )

          const pages = old.pages.map((page) => {
            // A row delete carries no value changes of its own, but the
            // values that belonged to that row still have to go.
            if (
              !valueChangesForRowValuesMap.size &&
              !Object.keys(valueIdChangesMap).length &&
              !deletedRowsIds.length
            )
              return page
            let updatedResults = page.results.map((storedValue) => {
              const newId = valueIdChangesMap[storedValue.id]
              if (newId) {
                delete valueIdChangesMap[storedValue.id]
                storedValue.id = newId
              }
              const valueChange = valueChangesForRowValuesMap.get(
                storedValue.id,
              )
              if (!valueChange) return storedValue

              const updatedValue = {
                ...storedValue,
                ...valueChange,
                in_cache: false,
                row_value_flags: updateFlags(
                  valueChange.flags,
                  storedValue.row_value_flags || [],
                  flagTypes,
                ),
              } as DocumentRowValue
              valueChangesForRowValuesMap.delete(storedValue.id)

              return updatedValue
            })

            if (deletedRowsIds.length) {
              updatedResults = updatedResults.filter(
                ({ document_row_id }) =>
                  !deletedRowsIds.includes(document_row_id),
              )
            }

            return {
              ...page,
              results: updatedResults,
            }
          })
          if (valueChangesForRowValuesMap.size) {
            const newValues = valueChangesForRowValuesMap.values().map(
              (valueChange) =>
                ({
                  ...valueChange,
                  row_value_flags: updateFlags(
                    valueChange.flags,
                    [],
                    flagTypes,
                  ),
                } as DocumentRowValue),
            )
            pages[pages.length - 1] = {
              ...pages[pages.length - 1],
              results: [...pages[pages.length - 1].results, ...newValues],
            }
          }
          return {
            ...old,
            pages,
          }
        })
      }

      // #region Update Chips
      const chipFieldKeysThatChanged: Set<FieldIdentifier> = new Set()
      const chipsAssignmentMap: Record<
        string,
        Pick<DocumentChip, 'id' | 'project_grid_field_id' | 'document_row_id'>
      > = {}

      // Get the chip assignment changes from the value changes
      const fieldsChangedBySystem: FieldIdentifier[] = []
      if (values.length) {
        const valueChangesMap = new Map(
          values.reduce<[string, NotificationValueChange][]>(
            (acc, valueChange) => [...acc, [valueChange.id, valueChange]],
            [],
          ),
        )

        const updatedRowValuesData = queryClient.getQueryData<
          InfiniteData<PaginatedResponse<DocumentRowValue>>
        >(rowValuesListQueryKey)

        updatedRowValuesData?.pages?.forEach((page) => {
          page.results.forEach(
            ({ id, project_grid_field_id, document_row_id }) => {
              const valueChange = valueChangesMap.get(id)
              if (!valueChange) return
              if (isSystemOriginatedValueChange(valueChange)) {
                fieldsChangedBySystem.push(
                  buildFieldIdentifier(project_grid_field_id, document_row_id),
                )
              }

              const valueChipIds = valueChange.doc_chip_ids

              // If the field doc_chips_ids was not included, it means the chips for the field didn't change.
              if (valueChipIds) {
                chipFieldKeysThatChanged.add(
                  buildFieldIdentifier(project_grid_field_id, document_row_id),
                )
                if (valueChipIds.length) {
                  valueChipIds.forEach((id) => {
                    chipsAssignmentMap[id] = {
                      id,
                      project_grid_field_id,
                      document_row_id,
                    }
                  })
                }
              }
            },
          )
        })
      }

      markFieldsChangedBySystem(fieldsChangedBySystem)

      if (
        chips.length ||
        chipFieldKeysThatChanged.size ||
        deletedRowsIds.length
      ) {
        const chipChangesMap = new Map(
          chips.reduce<[string, NotificationChipChange][]>(
            (acc, chipChange) => [...acc, [chipChange.id, chipChange]],
            [],
          ),
        )
        await queryClient.cancelQueries({ queryKey: chipsListQueryKey })
        queryClient.setQueryData<InfiniteData<PaginatedResponse<DocumentChip>>>(
          chipsListQueryKey,
          (old) => {
            if (!old) return old
            const pages = old.pages.map((page) => {
              return {
                ...page,
                results: page.results.map((storedChip) => {
                  let updatedChip = storedChip
                  const chipChange = chipChangesMap.get(storedChip.id)
                  if (chipChange) {
                    updatedChip = {
                      ...storedChip,
                      overridden_data: chipChange.overridden_data,
                    }
                  }

                  const chipFieldKey = buildFieldIdentifier(
                    storedChip.project_grid_field_id,
                    storedChip.document_row_id,
                  )
                  if (
                    (chipFieldKeysThatChanged.has(chipFieldKey) &&
                      !chipsAssignmentMap[storedChip.id]) ||
                    (storedChip.document_row_id &&
                      deletedRowsIds.includes(storedChip.document_row_id))
                  ) {
                    return {
                      ...updatedChip,
                      project_grid_field_id: null,
                      document_row_id: null,
                    }
                  }
                  if (chipsAssignmentMap[storedChip.id]) {
                    return {
                      ...updatedChip,
                      ...chipsAssignmentMap[storedChip.id],
                    }
                  }
                  return updatedChip
                }),
              }
            })
            return {
              ...old,
              pages,
            }
          },
        )
      }
      clearAllFieldsBeingUpdated()
    },
    [
      chipsListQueryKey,
      documentId,
      documentQueryKey,
      flagTypes,
      queryClient,
      queryKeysMapContext,
      resetData,
      rowValuesListQueryKey,
      rowsListQueryKey,
      clearAllFieldsBeingUpdated,
      markFieldsChangedBySystem,
    ],
  )

  const combineNotificationChanges = (newChange: NotificationChange) => {
    const prevChange = combinedNotificationChangeRef.current
    if (!prevChange) return newChange

    const invalidateData =
      requiresInvalidatingData(prevChange) ||
      requiresInvalidatingData(newChange)

    const combinedChange: NotificationChange = {
      change_set_ids: [
        ...prevChange.change_set_ids,
        ...newChange.change_set_ids,
      ],
      message: prevChange.message || newChange.message, // We want to keep the message, if it exists, to show the user that something went wrong.
      values: [],
      rows: [],
      tags: [],
      doc_flags: [],
      id_changes: [],
      chips: [],
    }

    if (!invalidateData) {
      combinedChange.owner_org_id =
        newChange.owner_org_id || prevChange.owner_org_id // We keep the new org id if it exists
      combinedChange.values = mergeObjectArrays(
        prevChange.values,
        newChange.values,
        'id',
        {
          keyInMainObject: 'flags',
          identifierKey: 'id',
        },
      )
      combinedChange.rows = mergeObjectArrays(
        prevChange.rows,
        newChange.rows,
        'id',
      )
      combinedChange.tags = mergeObjectArrays(
        prevChange.tags,
        newChange.tags,
        'project_tag_id',
      )
      combinedChange.doc_flags = mergeObjectArrays(
        prevChange.doc_flags,
        newChange.doc_flags,
        'id',
      )
      combinedChange.id_changes = [
        ...prevChange.id_changes,
        ...newChange.id_changes,
      ]
      combinedChange.chips = mergeObjectArrays(
        prevChange.chips,
        newChange.chips,
        'id',
      )
    }

    return combinedChange
  }

  const applyNotificationChange = useCallback(
    (notification: NotificationChange) => {
      if (!documentId) return

      const documentsChangeSetIds = documentsChangeSetIdsRef.current

      if (documentsChangeSetIds[documentId]?.size) {
        const updatedChangeSetIds = documentsChangeSetIds[documentId]
        notification.change_set_ids.forEach((id) => {
          updatedChangeSetIds.delete(id)
        })
        if (updatedChangeSetIds.size) {
          combinedNotificationChangeRef.current =
            combineNotificationChanges(notification)
        } else {
          updateDataCache(combineNotificationChanges(notification))
          combinedNotificationChangeRef.current = undefined
        }
      } else {
        updateDataCache(combineNotificationChanges(notification))
        combinedNotificationChangeRef.current = undefined
      }
    },
    [documentId, documentsChangeSetIdsRef, updateDataCache],
  )

  useEffect(() => {
    if (!documentId || !registerChangeApplier) return undefined
    return registerChangeApplier(documentId, applyNotificationChange)
  }, [documentId, registerChangeApplier, applyNotificationChange])

  useEffect(() => {
    if (!documentId) return

    clearAllFieldsBeingUpdated()
    clearSystemChanges()
    if (documentsChangeSetIdsRef.current[documentId]) {
      delete documentsChangeSetIdsRef.current[documentId]
    }
    setDocumentLastChangeSetSentAt?.((prev) => {
      if (!prev[documentId]) return prev
      const updated = { ...prev }
      delete updated[documentId]
      return updated
    })
    combinedNotificationChangeRef.current = undefined
  }, [
    documentId,
    documentsChangeSetIdsRef,
    setDocumentLastChangeSetSentAt,
    clearAllFieldsBeingUpdated,
    clearSystemChanges,
  ])

  return { applyNotificationChange, resetData }
}
